import React, { ChangeEvent, useRef, useState } from 'react';
import styled from 'styled-components';
import { CrossIcon, UploadIcon } from '../Icons';
import { Image } from '@src/interfaces/type';

const Container = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 658px;
	height: 155px;
	border: 1px dashed #ffbe00;
	border-radius: 8px;
	overflow-x: auto;
	@media screen and (max-width: 600px) {
		width: 90vw;
	}
`;

const ImageThumbnail = styled.div`
	display: flex;
	align-items: center;
	margin-left: 15px;
	gap: 15px;

	#no-image {
		display: flex;
		justify-content: center;
		align-items: center;
		color: var(--default-text-color);
	}

	@media screen and (max-width: 600px) {
		margin: 10px;
	}
`;

const ImageContainer = styled.div`
	position: relative;
	width: 113px;
	height: 113px;
	border-radius: 8px;
	border: 1.5px solid #e9e9e9;
	cursor: pointer;
	img {
		object-fit: contain;
	}
`;

const Cross = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: #f7f5f4;
	top: -8px;
	right: -8px;
	cursor: pointer;
`;
const UploadContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 113px;
	justify-content: flex-end;
	align-items: center;
	div {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 8px;
		padding-right: 12px;
		color: #333;
		font-size: 14px;
		font-weight: 600;
		line-height: 24px;
		cursor: pointer;
	}
`;
interface Props {
	images: Image[];
	setImages: React.Dispatch<React.SetStateAction<Image[]>>;
	setFiles: any;
	handleFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
	maxLength?: number;
	windowDimensions?: any;
	style?: React.CSSProperties;
	readOnly?: boolean;
	openPreview?: any;
	accept?: string;
}
const ImageThumnail: React.FC<Props> = ({
	images,
	setImages,
	setFiles,
	handleFileChange,
	windowDimensions = null,
	maxLength = 3,
	style = {},
	readOnly = false,
	openPreview = null,
					accept="image/png, image/jpeg, image/gif"
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const fade_in_out: React.CSSProperties = {
		opacity: 0,
		transition: 'opacity 0.5s ease-in-out',
	};

	const fade_in_out_show: React.CSSProperties = {
		opacity: 1,
	};

	const handleClick = () => {
		if (inputRef.current) {
			inputRef.current.click();
		}
	};

	const handleMouseEnter = (id: string) => {
		setImages((prevImages) =>
			prevImages.map((img) => (img?.id === id ? { ...img, hover: true } : img))
		);
	};

	const handleMouseLeave = (id: string) => {
		setImages((prevImages) =>
			prevImages.map((img) => (img?.id === id ? { ...img, hover: false } : img))
		);
	};

	const removeImage = (id: string, index: number) => {
		const updatedImages = images?.filter((img) => img?.id !== id);
		setImages(updatedImages);
		setFiles((prevState: any) => {
			const data = prevState?.filter((file: any, fileIndex: number) => fileIndex !== index);
			return data;
		});
	};

	if (readOnly) {
		return (
			<Container style={style}>
				<ImageThumbnail>
					{images?.length ? (
						images?.map((image, index) => {
							return (
								<ImageContainer
									key={image?.id}
									onMouseEnter={() => handleMouseEnter(image?.id)}
									onMouseLeave={() => handleMouseLeave(image?.id)}
								>
									{openPreview ? (
										<img
											onClick={openPreview}
											src={image?.url}
											width="100%"
											height="100%"
											alt="product-img"
										/>
									) : (
										<img src={image?.url} width="100%" height="100%" alt="product-img" />
									)}
								</ImageContainer>
							);
						})
					) : (
						<p id="no-image">Images not available for this product</p>
					)}
				</ImageThumbnail>
			</Container>
		);
	}

	return (
		<Container style={style}>
			<ImageThumbnail>
				{images?.map((image, index) => {
					return (
						<ImageContainer
							key={image?.id}
							onMouseEnter={() => handleMouseEnter(image?.id)}
							onMouseLeave={() => handleMouseLeave(image?.id)}
						>
							<img src={image?.url} width="100%" height="100%" alt="product-img" />
							{image?.hover && (
								<Cross
									style={image?.hover ? fade_in_out_show : fade_in_out}
									onClick={() => removeImage(image?.id, index)}
								>
									<CrossIcon />
								</Cross>
							)}
						</ImageContainer>
					);
				})}
			</ImageThumbnail>
			{maxLength > 1 ? (
				<UploadContainer>
					{images?.length < maxLength ? (
						<div onClick={handleClick}>
							<UploadIcon width="16px" height="16px" />
							{windowDimensions && windowDimensions?.width > 800 ? <p>Add Image</p> : null}
							<input
								ref={inputRef}
								type="file"
								multiple
								onChange={handleFileChange}
								hidden
								accept={accept}
							/>
						</div>
					) : null}
				</UploadContainer>
			) : null}
		</Container>
	);
};

export default ImageThumnail;
